// Hidden markers kept balanced after a merge (W9b; PROTOCOL.md §10.12). `rules.balancingHiddenSyntax` balances an edit
// before it's made, so one writer's typing never leaves a marker unpaired; but two writers' edits merged by Automerge
// can: one italicises a word while the other deletes it (`a *word* b` and `a b` merge to `a ** b`: bold to the end of
// the line), both put small caps on overlapping words (`[a [word]{.sc} b]{.sc}`: a `[` and a `]{.sc}` that pair with
// nothing show as text), or a line keeps nothing but markers (`***`, read as a scene break). After a remote change,
// the paragraphs it touched are checked and the markers that don't pair are **deleted** — nothing is ever inserted, and
// no character the reader sees is touched:
//
// - emphasis still open at the end of a line: one `*` from the last run that toggled italic (a run of 3 becomes bold
//   only, a run of 1 goes), then from the last run that toggled bold all but `n mod 2` of its `*`s;
// - span brackets showing as text that were markup in the copies merged (`markupInParents`, from the document's
//   history: a bracket a writer typed as text is never touched); without the history, a close (`]{…}`) that pairs
//   with nothing, with the nearest unpaired `[` before it, and a `[` whose span another `[` cuts short;
// - a line of nothing but markers that reads as a scene break, when every `*` of it was markup;
// - again, until the lines are balanced (a deletion can uncover another).
//
// Why only deletions: they're idempotent under merge. Two devices that fix the same merged text choose the same
// characters (the choice is a function of the text and which characters were markup, which every copy agrees on), and
// Automerge deletes a character once however many devices delete it, so the replicas still converge. An insertion made
// on both would be doubled — `**` from each, a bold toggle that cancels — which is why the fix never rebuilds markers
// as `rules.restyled` does. Devices that fix different intermediate merges may delete a marker more than needed (a
// style lost, never a character), and the next merge is checked again. `test/cowriting.fuzz.test.ts` checks
// convergence, balance and no loss over 10,000 random runs.
//
// When not to fix emphasis (`mergeFix`): a line a writer is typing markers into by hand (`*word`, or with marks shown)
// is open until they type the closing one. So emphasis is fixed only when neither side of the merge — this device's
// text before the change, and the sender's — had an open line the other didn't; lines open identically on both sides
// (an import's) are left to the writer.
import { Transaction, type EditorState, type TransactionSpec } from '@codemirror/state';
import { hiddenTester, isSceneBreak, lineSyntax, spanFromClasses, trimmed } from './markdown.ts';
import { layoutOf, Exact } from './state.ts';

export interface Deletion {
  from: number;
  to: number;
}

const STAR = 42, OPEN = 91, CLOSE = 93, CARET = 94, OBRACE = 123, CBRACE = 125, BACKSLASH = 92, NL = 10;

const lineStartOf = (t: string, i: number) => (i <= 0 ? 0 : t.lastIndexOf('\n', i - 1) + 1);
function lineEndOf(t: string, i: number) {
  const e = t.indexOf('\n', i);
  return e < 0 ? t.length : e;
}

export interface FixOptions {
  /** Close emphasis left open at a line's end (default true; off while a writer may be typing a marker). */
  emphasis?: boolean;
  /**
   * Whether the character at an offset of the merged text was markup (hidden syntax) in the copies merged — in each
   * that has it (`markupInParents`). With it, every span bracket that shows as text but was markup where it was made
   * goes, and a bracket a writer typed as text stays; without it, closes that pair with nothing go with the nearest
   * unpaired `[` before them, and a `[` cut short by another `[`.
   */
  wasMarkup?: (offset: number) => boolean;
}

/** A line's unpaired markers as deletions (absolute offsets in `t`, ascending, not overlapping): one round. */
export function lineFixes(t: string, lineStart: number, o: FixOptions = {}): Deletion[] {
  const lineEnd = lineEndOf(t, lineStart);
  const line = t.slice(lineStart, lineEnd);
  if (line.indexOf('*') < 0 && line.indexOf(']') < 0 && line.indexOf('[') < 0) return [];
  // Emphasis markers left alone on a line read as a scene break (`***`): gone, when every `*` was markup.
  if (o.wasMarkup && isSceneBreak(trimmed(line))) {
    const stars: number[] = [];
    for (let i = lineStart; i < lineEnd; i++) if (t.charCodeAt(i) === STAR) stars.push(i);
    return stars.every((i) => o.wasMarkup!(i)) ? normalized(stars.map((i) => ({ from: i, to: i + 1 }))) : [];
  }
  const syn = lineSyntax(t, lineStart);
  if (syn.prefix >= lineEnd - lineStart) return [];
  const out: Deletion[] = [];
  if (o.emphasis !== false) out.push(...emphasisFixes(t, syn.markers));
  // Spans: brackets the scanner didn't take as markers show as text.
  const hidden = (i: number) => syn.markers.some(([a, b]) => a <= i && i < b);
  const escaped = (i: number) => i > 0 && t.charCodeAt(i - 1) === BACKSLASH && hidden(i - 1);
  const contentStart = lineStart + syn.prefix;
  /** The end of a span close's classes starting at `i` (`]{.sc}`), or −1. */
  const closeEnd = (i: number) => {
    if (i + 1 >= lineEnd || t.charCodeAt(i + 1) !== OBRACE) return -1;
    let j = i + 2;
    while (j < lineEnd && t.charCodeAt(j) !== CBRACE) j++;
    return j < lineEnd && spanFromClasses(t.slice(i + 1, j + 1)) ? j + 1 : -1;
  };
  const visibleOpen = (k: number) => t.charCodeAt(k) === OPEN && !hidden(k) && !escaped(k) && !(k + 1 < lineEnd && t.charCodeAt(k + 1) === CARET);
  if (o.wasMarkup) {
    for (let i = contentStart; i < lineEnd; i++) {
      if (hidden(i) || escaped(i)) continue;
      if (visibleOpen(i) && o.wasMarkup(i)) out.push({ from: i, to: i + 1 });
      else if (t.charCodeAt(i) === CLOSE) {
        const e = closeEnd(i);
        if (e > 0 && o.wasMarkup(i)) {
          out.push({ from: i, to: e });
          i = e - 1;
        }
      }
    }
    return normalized(out);
  }
  const paired = new Set<number>();
  for (let i = contentStart; i < lineEnd; i++) {
    if (t.charCodeAt(i) !== CLOSE || hidden(i) || escaped(i)) continue;
    const e = closeEnd(i);
    if (e < 0) continue;
    out.push({ from: i, to: e });
    for (let k = i - 1; k >= contentStart; k--) {
      if (!visibleOpen(k) || paired.has(k)) continue;
      paired.add(k);
      out.push({ from: k, to: k + 1 });
      break;
    }
    i = e - 1;
  }
  // A `[` whose span is cut short by another `[` before any `]` (two spans merged one inside the other).
  for (let k = contentStart; k < lineEnd; k++) {
    if (!visibleOpen(k) || paired.has(k)) continue;
    let j = k + 1;
    while (j < lineEnd && t.charCodeAt(j) !== OPEN && t.charCodeAt(j) !== CLOSE) j += t.charCodeAt(j) === BACKSLASH ? 2 : 1;
    if (j < lineEnd && t.charCodeAt(j) === OPEN && hidden(j)) out.push({ from: k, to: k + 1 });
  }
  return normalized(out);
}

/** Emphasis left open at the line's end: one `*` from the last run that toggled italic, then all but `n mod 2` of
 *  the last run that toggled bold. */
function emphasisFixes(t: string, markers: [number, number][]): Deletion[] {
  const out: Deletion[] = [];
  const runs = markers.filter(([a]) => t.charCodeAt(a) === STAR).map(([a, b]) => ({ a, n: b - a, keep: b - a }));
  const italicRuns = runs.filter((r) => r.n % 2 === 1);
  if (italicRuns.length % 2 === 1) {
    const r = italicRuns[italicRuns.length - 1];
    out.push({ from: r.a + r.n - 1, to: r.a + r.n });
    r.keep = r.n - 1;
  }
  const boldRuns = runs.filter((r) => r.n >= 2);
  if (boldRuns.length % 2 === 1) {
    const r = boldRuns[boldRuns.length - 1];
    // From the run's end (where the italic deletion, if this run had it, already is): one range for the run.
    const from = r.a + (r.keep % 2);
    const existing = out.find((d) => d.from === r.a + r.n - 1);
    if (existing) existing.from = from;
    else out.push({ from, to: r.a + r.keep });
  }
  return out;
}

/** Whether a line's emphasis closes (what a writer typing a marker by hand leaves open for a while). */
const emphasisOpen = (t: string, lineStart: number) => {
  const line = t.slice(lineStart, lineEndOf(t, lineStart));
  return line.indexOf('*') >= 0 && emphasisFixes(t, lineSyntax(t, lineStart).markers).length > 0;
};

/** Sorted, merged, no empty ranges. */
function normalized(ds: Deletion[]): Deletion[] {
  const sorted = ds.filter((d) => d.to > d.from).sort((a, b) => a.from - b.from);
  const out: Deletion[] = [];
  for (const d of sorted) {
    const last = out[out.length - 1];
    if (last && d.from <= last.to) last.to = Math.max(last.to, d.to);
    else out.push({ ...d });
  }
  return out;
}

/** The line starts of the lines `[from, to]` touches. */
function linesOf(t: string, from: number, to: number): number[] {
  const out: number[] = [];
  let s = lineStartOf(t, Math.max(0, Math.min(from, t.length)));
  const end = Math.min(Math.max(from, to), t.length);
  for (;;) {
    out.push(s);
    const e = lineEndOf(t, s);
    if (e >= end || e >= t.length) break;
    s = e + 1;
  }
  return out;
}

/** Every deletion that balances the lines `[from, to]` touches, in offsets of `t` (rounds until nothing's left). */
export function rebalanceDeletions(t: string, from = 0, to = t.length, o: FixOptions = {}): Deletion[] {
  let text = t;
  // Each character of `text` → its offset in `t`.
  let origin: number[] | null = o.wasMarkup ? Array.from({ length: t.length }, (_, i) => i) : null;
  let a = from, b = to;
  for (let round = 0; round < 16; round++) {
    const map = origin;
    const opts: FixOptions = { emphasis: o.emphasis, ...(o.wasMarkup && map ? { wasMarkup: (i: number) => o.wasMarkup!(map[i]) } : {}) };
    const ds: Deletion[] = [];
    for (const s of linesOf(text, a, b)) ds.push(...lineFixes(text, s, opts));
    if (!ds.length) break;
    origin ??= Array.from({ length: t.length }, (_, i) => i);
    const sorted = normalized(ds);
    let next = '', at = 0;
    const kept: number[] = [];
    for (const d of sorted) {
      next += text.slice(at, d.from);
      for (let i = at; i < d.from; i++) kept.push(origin[i]);
      at = d.to;
    }
    next += text.slice(at);
    for (let i = at; i < text.length; i++) kept.push(origin[i]);
    // The touched range, in the new text.
    const shift = (p: number) => {
      let q = p;
      for (const d of sorted) if (d.from < p) q -= Math.min(p, d.to) - d.from;
      return q;
    };
    a = shift(a);
    b = Math.max(a, shift(b));
    text = next;
    origin = kept;
  }
  if (!origin || origin.length === t.length) return [];
  const survived = new Uint8Array(t.length);
  for (const i of origin) survived[i] = 1;
  const out: Deletion[] = [];
  for (let i = 0; i < t.length; i++) {
    if (survived[i]) continue;
    const last = out[out.length - 1];
    if (last && last.to === i) last.to = i + 1;
    else out.push({ from: i, to: i + 1 });
  }
  return out;
}

/** The text of every line with unpaired markers (lines without `*` or `]` are skipped unread). */
export function unbalancedLines(t: string): string[] {
  const out: string[] = [];
  let s = 0;
  for (;;) {
    const e = lineEndOf(t, s);
    if (lineFixes(t, s).length) out.push(t.slice(s, e));
    if (e >= t.length) break;
    s = e + 1;
  }
  return out;
}

/** Whether every line's markers pair (what the fix leaves, and what writers' own edits leave). */
export const isBalanced = (t: string) => unbalancedLines(t).length === 0;

/** The lines whose emphasis is left open (their text). */
function openEmphasisLines(t: string): string[] {
  const out: string[] = [];
  let s = 0;
  for (;;) {
    const e = lineEndOf(t, s);
    if (emphasisOpen(t, s)) out.push(t.slice(s, e));
    if (e >= t.length) break;
    s = e + 1;
  }
  return out;
}

/**
 * After a remote change merged a fragment's text from `before` (this device's) to `merged`: the deletions balancing
 * the lines `touched` covers (in `merged`). Emphasis is left alone when either side had a line with emphasis open that
 * the other didn't (a writer in the middle of typing a marker; `theirs` is the sender's text, null when unknown).
 * `wasMarkup`: see `FixOptions`.
 */
export function mergeFix(merged: string, touched: [number, number] | null, before: string, theirs: string | null,
  wasMarkup?: (offset: number) => boolean): Deletion[] {
  if (!touched) return [];
  const mine = openEmphasisLines(before);
  const other = theirs === null ? mine : openEmphasisLines(theirs);
  const own = (xs: string[], ys: string[]) => xs.some((l) => !ys.includes(l));
  const typing = own(mine, other) || own(other, mine);
  return rebalanceDeletions(merged, touched[0], touched[1], { emphasis: !typing, ...(wasMarkup ? { wasMarkup } : {}) });
}

/** What `FixOptions.wasMarkup` needs of a document's history: Automerge's backend (`A.getBackend(doc)`). */
export interface CursorBackend {
  getCursor(obj: string, index: number, heads?: string[]): string;
  getCursorPosition(obj: string, cursor: string, heads?: string[]): number;
}

/**
 * `wasMarkup` from the merged document's history: the character at an offset of the text now was markup in each of
 * `parents` (the copies merged: their heads and text) that has it. Offsets are UTF-16, as JavaScript Automerge's.
 */
export function markupInParents(backend: CursorBackend, textObj: string, parents: { heads: string[]; text: string }[]): (offset: number) => boolean {
  const testers = parents.map((p) => hiddenTester(p.text));
  return (offset) => {
    let cursor: string;
    try {
      cursor = backend.getCursor(textObj, offset);
    } catch {
      return false;
    }
    let found = false;
    for (let k = 0; k < parents.length; k++) {
      const { heads } = parents[k];
      let p: number;
      try {
        p = backend.getCursorPosition(textObj, cursor, heads);
        if (backend.getCursor(textObj, p, heads) !== cursor) continue; // not in this copy
      } catch {
        continue;
      }
      if (!testers[k](p)) return false; // text there
      found = true;
    }
    return found;
  };
}

/** Whether the lines `[from, to]` touches hold any marker character (`*`, `[`, `]`): when not, a merge there needs no
 *  fix, and nothing more need be read. */
export function touchesMarkup(t: string, from: number, to: number): boolean {
  const a = lineStartOf(t, Math.max(0, Math.min(from, t.length)));
  const b = lineEndOf(t, Math.min(Math.max(from, to), t.length));
  return /[*[\]]/.test(t.slice(a, b));
}

/** The smallest range of `b` that differs from `a` (what a remote change touched), or null when they're equal. */
export function changedRange(a: string, b: string): [number, number] | null {
  if (a === b) return null;
  let p = 0;
  const max = Math.min(a.length, b.length);
  while (p < max && a.charCodeAt(p) === b.charCodeAt(p)) p++;
  let s = 0;
  while (s < a.length - p && s < b.length - p && a.charCodeAt(a.length - 1 - s) === b.charCodeAt(b.length - 1 - s)) s++;
  return [p, b.length - s];
}

/** The fix as one transaction on the editor: the fragment's deletions, exact (never balanced again), undoable. */
export function rebalanceSpec(state: EditorState, fragmentId: string, deletions: Deletion[]): TransactionSpec | null {
  if (!deletions.length || state.readOnly) return null;
  const layout = layoutOf(state);
  const i = layout.index(fragmentId);
  if (i === undefined || layout.isScreenplay(i)) return null;
  const base = layout.from[i], length = layout.to[i] - base;
  if (deletions.some((d) => d.from < 0 || d.to > length)) return null;
  return {
    changes: deletions.map((d) => ({ from: base + d.from, to: base + d.to })),
    annotations: [Exact.of(true), Transaction.userEvent.of('input.rebalance')],
  };
}
